/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import classNames from 'classnames';

import { ChevronDown } from 'lucide-react';
import UDText from 'ud-ui/components/text';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { theme } from 'styles/theme/theme';
import { DivProps } from 'typings';
import { UDInputProps } from '../../component';
import { phonePrefixes } from '../../domain/constants/phone-prefixes';
import { formatPhone, ValidPhoneCode } from '../../domain/formatters/format-phone';
import { parsePhone } from '../../domain/formatters/parse-phone';
import { getPhoneMask } from '../../domain/helpers/get-phone-mask';
import { Dropdown } from '../dropdown';
import { Container, DropdownItem, Input, Prefix } from '../styles';

export type UDInputPhoneProps = UDInputProps & {
  value: string;
  numberPart: string;
  hasError?: boolean;
  valid?: boolean;
  isOpen: boolean;
  activeOption: (typeof phonePrefixes)[0];
  placeholder?: string;
  wrapperProps?: Omit<DivProps, 'children'>;
  onPrefixClick: () => void;
  onChangeHandler: (value: string) => void;
  selectOption: (option: (typeof phonePrefixes)[0]) => void;
  onBlur?: (e: React.FocusEvent<any>) => void;
};

const ContainerWithRef = forwardRef<HTMLDivElement, DivProps & { readOnly?: boolean }>((props, ref) => (
  <Container {...props} ref={ref} />
));

const UDInputPhoneComponent = (props: UDInputPhoneProps) => {
  const {
    hasError,
    valid,
    isOpen,
    activeOption,
    numberPart,
    placeholder,
    wrapperProps,
    onPrefixClick,
    onChangeHandler,
    selectOption,
    onBlur,
    readOnly,
    ...rest
  } = props;

  const parentRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const isValid = useMemo(() => {
    if (hasError || !numberPart) {
      return false;
    }
    const numberPartWithoutPrefix = numberPart?.replace(/\D/g, '');
    return numberPartWithoutPrefix.length === getPhoneMask(activeOption.prefix as ValidPhoneCode).replace(/[^⦁]/g, '').length;
  }, [hasError, numberPart, activeOption.prefix]);

  const options = phonePrefixes.map((option) => (
    <DropdownItem key={option.id} isActive={activeOption.id === option.id} onClick={() => selectOption(option)}>
      <option.icon className="icon" />
      <UDText type="subhead" className="country">
        {option.name}
      </UDText>
      <UDText type="subhead" className="prefix">
        {option.prefix}
      </UDText>
    </DropdownItem>
  ));

  return (
    <div className={classNames('relative w-full', wrapperProps?.className)}>
      <ContainerWithRef
        {...(wrapperProps as DivProps)}
        readOnly={readOnly}
        className={classNames({ error: hasError, valid: isValid, '--error': hasError })}
        ref={parentRef}
      >
        <Prefix onClick={onPrefixClick}>
          <activeOption.icon />
          <UDText type="subhead" style={{ fontFamily: 'SF Pro Text', letterSpacing: 1 }}>
            {activeOption.prefix}
          </UDText>
          <div className="flex-center" style={{ width: 24, height: 24 }}>
            <ChevronDown
              size={13}
              color={theme.colors.DarkBlue}
              style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              css={css`
                transition: transform 0.25s ease;
              `}
            />
          </div>
        </Prefix>
        <Input
          {...rest}
          readOnly={readOnly}
          placeholder={placeholder}
          formatter={(value) => formatPhone(value, activeOption.prefix as ValidPhoneCode, isFocused)}
          parser={(value) => parsePhone(value, activeOption.prefix as ValidPhoneCode)}
          onChange={(event) => onChangeHandler(event.target.value)}
          onFocus={(e) => {
            setIsFocused(true);
            rest.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          className={classNames('input', rest.className)}
          wrapperProps={{ className: classNames('w-100') }}
          value={numberPart}
          mask={getPhoneMask(activeOption.prefix as ValidPhoneCode)}
          type="tel"
          inputMode="numeric"
        />
      </ContainerWithRef>
      <Dropdown parentRef={parentRef} isOpen={isOpen} options={options} />
    </div>
  );
};

export default UDInputPhoneComponent;
